import React, { useMemo } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { listToolLocations } from '../../services/toolLocationService';

/**
 * LocationPicker
 * --------------
 * Dropdown for selecting a `tool_locations.id` UUID. Groups options by type
 * and renders sub-locations indented beneath their parent. If `projectFilter`
 * is provided, locations attached to that project are surfaced alongside the
 * global ones.
 *
 * Props:
 * - value: string | null   — currently selected location_id (UUID).
 * - onChange: (id) => void — called with the new UUID (empty string when cleared).
 * - disabled?: boolean
 * - placeholder?: string   — first-option label (default: "Sin ubicación").
 * - projectFilter?: string — UUID of a project; locations for that project are
 *   included in addition to globals.
 * - showCount?: boolean    — render "(N ubicaciones)" helper text. Default true.
 * - id?: string            — Form.Select id (for label-htmlFor pairing).
 * - searchable?: boolean   — show a text input above the select that filters
 *   options by label/code. Default false.
 */

const TYPE_LABEL = {
  zona: 'Zonas',
  bodega: 'Bodegas',
  vehiculo: 'Vehículos',
  proyecto: 'Proyectos',
  sub: 'Sub-ubicaciones',
  otro: 'Otras'
};

const TYPE_ORDER = ['zona', 'bodega', 'vehiculo', 'proyecto', 'sub', 'otro'];

const LocationPicker = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Sin ubicación',
  projectFilter,
  showCount = true,
  id,
  searchable = false
}) => {
  const [search, setSearch] = React.useState('');

  // Pull globals (no project filter) and the project's locations in parallel.
  // We merge them client-side so the picker shows both lists without duplicates.
  const globalsQuery = useQuery({
    queryKey: ['tool-locations', 'globals'],
    queryFn: () => listToolLocations({ active_only: true }),
    staleTime: 60 * 1000
  });

  const projectQuery = useQuery({
    queryKey: ['tool-locations', 'project', projectFilter],
    queryFn: () => listToolLocations({ active_only: true, project_id: projectFilter }),
    enabled: !!projectFilter,
    staleTime: 60 * 1000
  });

  const isLoading = globalsQuery.isLoading || (!!projectFilter && projectQuery.isLoading);
  const error = globalsQuery.error || projectQuery.error;

  const locations = useMemo(() => {
    const seen = new Set();
    const merged = [];
    const pushUnique = (list) => {
      (list || []).forEach((loc) => {
        if (!seen.has(loc.id)) {
          seen.add(loc.id);
          merged.push(loc);
        }
      });
    };
    pushUnique(globalsQuery.data?.data);
    if (projectFilter) pushUnique(projectQuery.data?.data);
    return merged;
  }, [globalsQuery.data, projectQuery.data, projectFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return locations;
    const s = search.trim().toLowerCase();
    return locations.filter(
      (l) =>
        (l.label || '').toLowerCase().includes(s) ||
        (l.code || '').toLowerCase().includes(s)
    );
  }, [locations, search]);

  // Group by type, then nest children under their parent.
  const grouped = useMemo(() => {
    const byParent = new Map(); // parent_id (or 'root') -> [locations]
    filtered.forEach((loc) => {
      const key = loc.parent_location_id || 'root';
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(loc);
    });

    const byType = new Map(); // type -> [locations in display order]
    const visit = (loc, depth) => {
      const arr = byType.get(loc.type) || [];
      arr.push({ ...loc, _depth: depth });
      byType.set(loc.type, arr);
      const children = byParent.get(loc.id) || [];
      children.forEach((child) => visit(child, depth + 1));
    };

    const roots = byParent.get('root') || [];
    roots.forEach((loc) => visit(loc, 0));

    // Orphans: parent isn't visible (e.g. filtered out by search) — show flat.
    filtered.forEach((loc) => {
      const alreadyShown = (byType.get(loc.type) || []).some((x) => x.id === loc.id);
      if (!alreadyShown) {
        const arr = byType.get(loc.type) || [];
        arr.push({ ...loc, _depth: 0 });
        byType.set(loc.type, arr);
      }
    });

    return TYPE_ORDER
      .filter((t) => byType.has(t))
      .map((t) => ({ type: t, label: TYPE_LABEL[t], items: byType.get(t) }));
  }, [filtered]);

  const formatOption = (loc) => {
    const indent = loc._depth > 0 ? `${'  '.repeat(loc._depth)}↳ ` : '';
    return `${indent}${loc.label}`;
  };

  return (
    <>
      {searchable && (
        <Form.Control
          type="search"
          size="sm"
          placeholder="Buscar ubicación..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled}
          className="mb-2"
          aria-label="Buscar ubicación"
        />
      )}
      <Form.Select
        id={id}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || isLoading}
        aria-label="Ubicación"
      >
        <option value="">{placeholder}</option>
        {grouped.map((group) => (
          <optgroup key={group.type} label={group.label}>
            {group.items.map((loc) => (
              <option key={loc.id} value={loc.id}>{formatOption(loc)}</option>
            ))}
          </optgroup>
        ))}
      </Form.Select>
      {isLoading && (
        <Form.Text className="text-muted">
          <Spinner size="sm" animation="border" className="me-1" />Cargando ubicaciones...
        </Form.Text>
      )}
      {error && (
        <Form.Text className="text-danger">
          Error al cargar ubicaciones: {error.message}
        </Form.Text>
      )}
      {!isLoading && !error && showCount && (
        <Form.Text className="text-muted">
          ({filtered.length} {filtered.length === 1 ? 'ubicación' : 'ubicaciones'})
        </Form.Text>
      )}
    </>
  );
};

export default LocationPicker;
